import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useApi } from '@/utils/axios';
import { CONFIGURACAO_ROTAS } from '@/utils/apiRoutes';

export const useConfiguracaoStore = defineStore('configuracao', () => {
  // State
  const configuracoes = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const configLoaded = ref(false);

  // Computed
  const getConfiguracao = computed(() => (codigo) => {
    return configuracoes.value.find(config => config.codigo === codigo);
  });

  const getConfiguracaoValue = computed(() => (codigo, defaultValue = null) => {
    const config = configuracoes.value.find(config => config.codigo === codigo);
    return config ? config.conteudo : defaultValue;
  });

  // Actions
  const fetchConfiguracoes = async () => {
    if (configLoaded.value) return configuracoes.value;
    
    loading.value = true;
    error.value = null;
    
    try {
      const response = await useApi(CONFIGURACAO_ROTAS.LISTAR_CONFIGURACOES).fetch();
      
      // Handle the non-paginated response format
      if (response && response.success && Array.isArray(response.data)) {
        configuracoes.value = response.data;
        configLoaded.value = true;
      } else {
        throw new Error('Formato de resposta inválido');
      }
      
      return configuracoes.value;
    } catch (err) {
      error.value = err.message || 'Erro ao carregar configurações';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchConfiguracao = async (codigo) => {
    loading.value = true;
    error.value = null;
    
    try {
      const response = await useApi(CONFIGURACAO_ROTAS.CONFIGURACAO(codigo)).fetch();
      
      let configData = null;
      
      // Handle different possible response formats
      if (response && response.success && response.data) {
        configData = response.data;
      } else if (response && !response.success) {
        throw new Error(response.message || `Configuração ${codigo} não encontrada`);
      } else if (response) {
        configData = response;
      }
      
      if (configData) {
        // Update or add the configuration to the local list
        const index = configuracoes.value.findIndex(config => config.codigo === codigo);
        if (index !== -1) {
          configuracoes.value[index] = configData;
        } else {
          configuracoes.value.push(configData);
        }
        return configData;
      }
      
      return null;
    } catch (err) {
      error.value = err.message || `Erro ao carregar configuração ${codigo}`;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createConfiguracao = async (configData) => {
    loading.value = true;
    error.value = null;
    
    try {
      const response = await useApi(CONFIGURACAO_ROTAS.CRIAR_CONFIGURACAO).post(configData);
      
      let newConfig = null;
      
      if (response && response.success && response.data) {
        newConfig = response.data;
      } else if (response) {
        newConfig = response;
      }
      
      if (newConfig) {
        configuracoes.value.push(newConfig);
        return newConfig;
      }
      
      return null;
    } catch (err) {
      error.value = err.message || 'Erro ao criar configuração';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateConfiguracao = async (codigo, configData) => {
    loading.value = true;
    error.value = null;
    
    try {
      const response = await useApi(CONFIGURACAO_ROTAS.ATUALIZAR_CONFIGURACAO(codigo)).put(configData);
      
      let updatedConfig = null;
      
      if (response && response.success && response.data) {
        updatedConfig = response.data;
      } else if (response) {
        updatedConfig = response;
      }
      
      if (updatedConfig) {
        // Update the configuration in the local list
        const index = configuracoes.value.findIndex(config => config.codigo === codigo);
        if (index !== -1) {
          configuracoes.value[index] = updatedConfig;
        } else {
          configuracoes.value.push(updatedConfig);
        }
        return updatedConfig;
      }
      
      return null;
    } catch (err) {
      error.value = err.message || `Erro ao atualizar configuração ${codigo}`;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    // State
    configuracoes,
    loading,
    error,
    
    // Computed
    getConfiguracao,
    getConfiguracaoValue,
    
    // Actions
    fetchConfiguracoes,
    fetchConfiguracao,
    createConfiguracao,
    updateConfiguracao
  };
});
